import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { AppWindow, Database, Layers, Loader2, Moon, Sun } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { ConfirmActionDialog, RelativeTime, SectionCard, StatusBadge, StatusTone, formatCurrency, formatDateTime } from '../../../components/design-system/admin';
import { useDevEnvironmentApi } from '../../../common/hooks/use-dev-environment-api.hook';
import type { DevEnvResource, DevEnvState, DevEnvStatus } from '../../../common/models/dev-environment.model';

dayjs.extend(relativeTime);

const TONE: Record<DevEnvState, StatusTone> = {
  awake: 'success',
  asleep: 'neutral',
  waking: 'info',
  sleeping: 'info',
  mixed: 'warning',
  unconfigured: 'neutral',
  error: 'danger',
};
const IDLE_CHOICES = [1, 2, 3, 4, 6, 8, 12];
/** Poll fast while a wake or sleep runs, slowly otherwise. */
const FAST_MS = 5_000;
const SLOW_MS = 60_000;

const ICON: Record<DevEnvResource['kind'], typeof Database> = { vm: Database, plan: Layers, app: AppWindow };

/**
 * The dev environment's switch: asleep it costs ~€20 a month, awake ~€0.07 an
 * hour. Shows what is running, the traffic that keeps it awake, and when it
 * will sleep on its own.
 */
export function DevEnvironmentCard({ className }: { className?: string }) {
  const { t } = useTranslation();
  const api = useDevEnvironmentApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [data, setData] = useState<DevEnvStatus | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmSleep, setConfirmSleep] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await apiRef.current.status());
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  const moving = data?.state === 'waking' || data?.state === 'sleeping';
  useEffect(() => {
    load();
    const id = setInterval(() => document.visibilityState === 'visible' && load(), moving ? FAST_MS : SLOW_MS);
    return () => clearInterval(id);
  }, [load, moving]);

  const act = async (action: 'wake' | 'sleep') => {
    setBusy(true);
    try {
      setData(await (action === 'wake' ? apiRef.current.wake() : apiRef.current.sleep()));
    } catch (e: any) {
      toast.error(e?.message || t('ADMIN_COSTS.DEV_ENV.ACTION_FAILED'));
    } finally {
      setBusy(false);
      setConfirmSleep(false);
    }
  };

  const save = async (patch: Partial<DevEnvStatus['settings']>) => {
    try {
      setData(await apiRef.current.updateSettings(patch));
    } catch {
      toast.error(t('ADMIN_COSTS.DEV_ENV.SAVE_FAILED'));
    }
  };

  const state = data?.state;
  const title = (
    <span className="inline-flex items-center gap-2">
      {t('ADMIN_COSTS.DEV_ENV.TITLE')}
      {state && <StatusBadge tone={TONE[state]}>{t(`ADMIN_COSTS.DEV_ENV.STATE.${state}`)}</StatusBadge>}
    </span>
  );

  if (!data) {
    return (
      <SectionCard title={title} description={t('ADMIN_COSTS.DEV_ENV.DESCRIPTION')} className={className} testId="dev-env">
        {failed ? <p className="text-sm text-muted-foreground">{t('ADMIN_SHELL.COMMON.LOAD_FAILED')}</p> : <Skeleton className="h-40 w-full" />}
      </SectionCard>
    );
  }

  if (state === 'unconfigured') {
    return (
      <SectionCard title={title} description={t('ADMIN_COSTS.DEV_ENV.DESCRIPTION')} className={className} testId="dev-env">
        <p className="text-sm text-muted-foreground">{t('ADMIN_COSTS.DEV_ENV.UNCONFIGURED')}</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(data.missing || []).map((v) => (
            <code key={v} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">
              {v}
            </code>
          ))}
        </div>
      </SectionCard>
    );
  }

  const peak = Math.max(...data.activity.map((a) => a.requests), data.settings.quietBelow, 1);
  const headline =
    state === 'awake'
      ? data.settings.autoSleep && data.sleepsAt
        ? t('ADMIN_COSTS.DEV_ENV.SLEEPS_AT', { time: dayjs(data.sleepsAt).format('HH:mm'), when: dayjs(data.sleepsAt).fromNow() })
        : t('ADMIN_COSTS.DEV_ENV.AWAKE_MANUAL')
      : state === 'asleep'
        ? t('ADMIN_COSTS.DEV_ENV.ASLEEP_SINCE', { when: data.lastSleepAt ? dayjs(data.lastSleepAt).fromNow() : '—' })
        : moving
          ? t(`ADMIN_COSTS.DEV_ENV.STEP.${data.transition?.action || 'wake'}.${data.transition?.step || 'starting'}`)
          : state === 'mixed'
            ? t('ADMIN_COSTS.DEV_ENV.MIXED')
            : t('ADMIN_COSTS.DEV_ENV.ERROR', { error: data.error || '' });

  return (
    <SectionCard
      title={title}
      description={t('ADMIN_COSTS.DEV_ENV.DESCRIPTION')}
      className={className}
      testId="dev-env"
      action={
        <div className="flex items-center gap-2">
          {(state === 'asleep' || state === 'mixed' || state === 'error') && (
            <Button variant="outline" size="sm" onClick={() => act('wake')} disabled={busy} data-testid="dev-env-wake">
              <Sun />
              {t('ADMIN_COSTS.DEV_ENV.WAKE')}
            </Button>
          )}
          {(state === 'awake' || state === 'mixed') && (
            <Button variant="outline" size="sm" onClick={() => setConfirmSleep(true)} disabled={busy} data-testid="dev-env-sleep">
              <Moon />
              {t('ADMIN_COSTS.DEV_ENV.SLEEP')}
            </Button>
          )}
          {moving && (
            <Button variant="outline" size="sm" disabled>
              <Loader2 className="animate-spin" />
              {t(`ADMIN_COSTS.DEV_ENV.STATE.${state}`)}
            </Button>
          )}
        </div>
      }
    >
      <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
        <div className="flex flex-col gap-4">
          <p className="text-sm" data-testid="dev-env-headline">
            {headline}
          </p>

          <ul className="flex flex-col divide-y rounded-lg border">
            {data.resources.map((r) => {
              const Icon = ICON[r.kind];
              return (
                <li key={`${r.kind}:${r.name}`} className="flex items-center justify-between gap-3 px-3 py-2">
                  <span className="flex min-w-0 items-center gap-2.5">
                    <Icon className="size-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0">
                      <span className="block truncate font-mono text-xs">{r.name}</span>
                      <span className="block text-[11px] text-muted-foreground">
                        {t(`ADMIN_COSTS.DEV_ENV.KIND.${r.kind}`)}
                        {r.hosts?.length ? ` · ${r.hosts.join(' · ')}` : ''}
                      </span>
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5 text-xs">
                    <span className={cn('size-1.5 rounded-full', r.awake ? 'bg-success' : 'bg-muted-foreground/50')} aria-hidden />
                    {r.state}
                  </span>
                </li>
              );
            })}
          </ul>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex items-center gap-2">
              <Switch id="dev-env-auto" checked={data.settings.autoSleep} onCheckedChange={(v) => save({ autoSleep: v })} />
              <Label htmlFor="dev-env-auto" className="text-sm font-normal">
                {t('ADMIN_COSTS.DEV_ENV.AUTO_SLEEP')}
              </Label>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{t('ADMIN_COSTS.DEV_ENV.AFTER')}</span>
              <NativeSelect
                size="sm"
                aria-label={t('ADMIN_COSTS.DEV_ENV.AFTER')}
                value={String(data.settings.idleHours)}
                disabled={!data.settings.autoSleep}
                onChange={(e) => save({ idleHours: Number(e.target.value) })}
              >
                {IDLE_CHOICES.map((h) => (
                  <NativeSelectOption key={h} value={h}>
                    {t('ADMIN_COSTS.DEV_ENV.HOURS', { count: h })}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              <span>{t('ADMIN_COSTS.DEV_ENV.QUIET')}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div>
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-xs font-medium text-muted-foreground">{t('ADMIN_COSTS.DEV_ENV.ACTIVITY')}</p>
              <p className="text-[11px] text-muted-foreground">{t('ADMIN_COSTS.DEV_ENV.QUIET_LINE', { count: data.settings.quietBelow })}</p>
            </div>
            <div className="relative mt-2 flex h-20 items-end gap-[3px]" data-testid="dev-env-activity" aria-hidden>
              <div className="absolute inset-x-0 border-t border-dashed border-muted-foreground/40" style={{ bottom: `${(data.settings.quietBelow / peak) * 100}%` }} />
              {data.activity.map((a) => (
                <div
                  key={a.hour}
                  title={`${formatDateTime(a.hour)} · ${a.requests}`}
                  className={cn('flex-1 rounded-t-[2px]', a.requests >= data.settings.quietBelow ? 'bg-foreground/70' : 'bg-muted-foreground/25')}
                  style={{ height: `${Math.max((a.requests / peak) * 100, a.requests ? 3 : 1)}%` }}
                />
              ))}
            </div>
            <div className="mt-1 flex justify-between text-[10px] text-muted-foreground tabular-nums">
              <span>{data.activity[0] ? dayjs(data.activity[0].hour).format('HH:mm') : ''}</span>
              <span>{t('ADMIN_COSTS.DEV_ENV.NOW')}</span>
            </div>
          </div>

          <dl className="grid grid-cols-3 gap-2 text-xs">
            <div>
              <dt className="text-muted-foreground">{t('ADMIN_COSTS.DEV_ENV.AWAKE_COST')}</dt>
              <dd className="mt-0.5 font-medium tabular-nums">{t('ADMIN_COSTS.DEV_ENV.PER_HOUR', { amount: formatCurrency(data.rates.awakePerHourEur, 'eur', 3) })}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('ADMIN_COSTS.DEV_ENV.ASLEEP_COST')}</dt>
              <dd className="mt-0.5 font-medium tabular-nums">{t('ADMIN_COSTS.DEV_ENV.PER_MONTH', { amount: formatCurrency(data.rates.asleepPerMonthEur, 'eur', 0) })}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('ADMIN_COSTS.DEV_ENV.SAVED')}</dt>
              <dd className="mt-0.5 font-medium tabular-nums" data-testid="dev-env-saved">
                {formatCurrency(data.month.savedEur, 'eur')}
                <span className="ml-1 font-normal text-muted-foreground">{t('ADMIN_COSTS.DEV_ENV.ASLEEP_HOURS', { count: Math.round(data.month.hoursAsleep) })}</span>
              </dd>
            </div>
          </dl>

          {data.history.length > 0 && (
            <ol className="flex flex-col gap-1.5 text-xs" data-testid="dev-env-history">
              {data.history.slice(0, 4).map((h, i) => (
                <li key={`${h.at}-${i}`} className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1.5">
                    {h.action === 'wake' ? <Sun className="size-3.5 shrink-0 text-muted-foreground" /> : <Moon className="size-3.5 shrink-0 text-muted-foreground" />}
                    <span className={cn('truncate', !h.ok && 'text-destructive')}>
                      {t(`ADMIN_COSTS.DEV_ENV.HISTORY.${h.action}${h.ok ? '' : '_FAILED'}`, { by: h.by === 'auto' ? t('ADMIN_COSTS.DEV_ENV.AUTO') : h.by })}
                    </span>
                  </span>
                  <RelativeTime value={h.at} className="shrink-0 text-muted-foreground" />
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      <ConfirmActionDialog
        open={confirmSleep}
        onOpenChange={setConfirmSleep}
        title={t('ADMIN_COSTS.DEV_ENV.CONFIRM_TITLE')}
        description={t('ADMIN_COSTS.DEV_ENV.CONFIRM_BODY')}
        confirmLabel={t('ADMIN_COSTS.DEV_ENV.SLEEP')}
        busy={busy}
        onConfirm={() => act('sleep')}
      />
    </SectionCard>
  );
}
